// client/src/components/pages/LexiStar/Notifications/inboxModel.ts (ST-00251)
//
// The notifications inbox's filter model and its pure helpers: what the URL says (every filter
// lives in the address, so a filtered view can be bookmarked or sent on), what the API is asked,
// how a notification type reads, and what a row is about.
//
// URL parameters: severity (C,H,…), type, rule, truck, driver, route, site, status
// (unread | read), from, to (YYYY-MM-DD, SAST days), q.
//
// ST-00280: a type reads in the words of the shared label catalogue (utils/eventTypeLabels).

import type {
  InboxFacetOption,
  InboxFacets,
  InboxFilterParams,
  InboxNotification,
  InboxSeverity,
  InboxStatus,
} from '../../../../types/lexistar-notification-inbox';
import { eventCategoryLabel, eventCategoryOf, eventTypeLabel } from '../../../../utils/eventTypeLabels';

export const SEVERITY_ORDER: InboxSeverity[] = ['C', 'H', 'M', 'L', 'I'];

export const SEVERITY_META: Record<InboxSeverity, { label: string; chip: string; dot: string }> = {
  C: { label: 'Critical', chip: 'bg-red-600 text-red-50', dot: 'bg-red-600' },
  H: { label: 'High', chip: 'bg-orange-100 text-orange-800', dot: 'bg-orange-500' },
  M: { label: 'Medium', chip: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' },
  L: { label: 'Low', chip: 'bg-blue-50 text-blue-700', dot: 'bg-blue-500' },
  I: { label: 'Info', chip: 'bg-slate-100 text-slate-600', dot: 'bg-slate-400' },
};

export const severityOf = (s: string | null | undefined): InboxSeverity =>
  (SEVERITY_ORDER as string[]).includes(String(s)) ? (s as InboxSeverity) : 'I';

/** The single-value dimensions (one choice each, from the facet lists). */
export const DIMENSIONS = ['type', 'rule', 'truck', 'driver', 'route', 'site'] as const;
export type Dimension = (typeof DIMENSIONS)[number];

export const DIMENSION_META: Record<
  Dimension,
  { label: string; all: string; facet: keyof Pick<InboxFacets, 'types' | 'rules' | 'trucks' | 'drivers' | 'routes' | 'sites'>; api: keyof InboxFilterParams }
> = {
  type: { label: 'Type', all: 'All types', facet: 'types', api: 'type' },
  rule: { label: 'Rule', all: 'All rules', facet: 'rules', api: 'rule_id' },
  truck: { label: 'Truck', all: 'All trucks', facet: 'trucks', api: 'truck' },
  driver: { label: 'Driver', all: 'All drivers', facet: 'drivers', api: 'driver_id' },
  route: { label: 'Route', all: 'All routes', facet: 'routes', api: 'route_id' },
  site: { label: 'Site', all: 'All sites', facet: 'sites', api: 'site_id' },
};

export interface InboxFilters {
  severity: InboxSeverity[];
  type: string;
  rule: string;
  truck: string;
  driver: string;
  route: string;
  site: string;
  status: '' | InboxStatus;
  from: string;
  to: string;
  q: string;
}

export const EMPTY_FILTERS: InboxFilters = {
  severity: [],
  type: '',
  rule: '',
  truck: '',
  driver: '',
  route: '',
  site: '',
  status: '',
  from: '',
  to: '',
  q: '',
};

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The filters the address carries (anything unrecognised is ignored). */
export function filtersFromParams(sp: URLSearchParams): InboxFilters {
  const one = (k: string) => (sp.get(k) || '').trim();
  const status = one('status');
  const day = (k: string) => (DAY_RE.test(one(k)) ? one(k) : '');
  return {
    severity: one('severity')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter((s, i, a): s is InboxSeverity => (SEVERITY_ORDER as string[]).includes(s) && a.indexOf(s) === i),
    type: one('type'),
    rule: one('rule'),
    truck: one('truck'),
    driver: one('driver'),
    route: one('route'),
    site: one('site'),
    status: status === 'unread' || status === 'read' ? status : '',
    from: day('from'),
    to: day('to'),
    q: one('q').slice(0, 200),
  };
}

/** The URL parameters of `f` (empty ones left out), in a stable order. */
export function paramsFromFilters(f: InboxFilters): Record<string, string> {
  const out: Record<string, string> = {};
  if (f.severity.length) out.severity = SEVERITY_ORDER.filter((s) => f.severity.includes(s)).join(',');
  for (const d of DIMENSIONS) if (f[d]) out[d] = f[d];
  if (f.status) out.status = f.status;
  if (f.from) out.from = f.from;
  if (f.to) out.to = f.to;
  if (f.q) out.q = f.q;
  return out;
}

/** What the API is asked for `f`. */
export function apiFilters(f: InboxFilters): InboxFilterParams {
  const out: InboxFilterParams = {};
  if (f.severity.length) out.severity = SEVERITY_ORDER.filter((s) => f.severity.includes(s));
  if (f.type) out.type = f.type;
  if (f.rule) out.rule_id = f.rule;
  if (f.truck) out.truck = f.truck;
  if (f.driver) out.driver_id = f.driver;
  if (f.route) out.route_id = f.route;
  if (f.site) out.site_id = f.site;
  if (f.status) out.status = f.status;
  if (f.from) out.from = f.from;
  if (f.to) out.to = f.to;
  if (f.q) out.q = f.q;
  return out;
}

/** Filters set away from "everything" (the read-state toggle counts as one). */
export function activeFilterCount(f: InboxFilters): number {
  return (
    (f.severity.length ? 1 : 0) +
    DIMENSIONS.filter((d) => !!f[d]).length +
    (f.status ? 1 : 0) +
    (f.from || f.to ? 1 : 0) +
    (f.q ? 1 : 0)
  );
}

/**
 * "ROUTE.DEVIATION_OPENED" → "Left the approved route" (category "Routes and deviations"): the
 * one label catalogue (utils/eventTypeLabels, ST-00280); the inbox's own kinds besides.
 */
export function typeLabel(type: string | null | undefined): { name: string; category: string | null } {
  if (!type) return { name: 'Notification', category: null };
  if (type === 'DIGEST') return { name: 'Digest of several alerts', category: null };
  const cat = eventCategoryOf(type);
  if (cat === type) return { name: eventTypeLabel(type), category: null };
  return { name: eventTypeLabel(type), category: eventCategoryLabel(cat) };
}

export const typeText = (type: string | null | undefined): string => {
  const t = typeLabel(type);
  return t.category ? `${t.category} · ${t.name}` : t.name;
};

/** What to show for a facet choice (types are labelled here, the rest by the server). */
export function optionLabel(dim: Dimension, o: Pick<InboxFacetOption, 'value' | 'label'>): string {
  if (dim === 'type') return typeText(o.value);
  return o.label || (dim === 'truck' ? o.value.replace(/^(reg|asset):/, '') : 'Unnamed');
}

/** One "about" item of a row: the filter it can apply, when it has one. */
export interface RelatedItem {
  key: 'truck' | 'driver' | 'trip' | 'route' | 'site';
  label: string;
  text: string;
  /** [dimension, value] to filter by, when this item is a filter. */
  filter?: [Dimension, string];
}

/** What a notification is about, in reading order. */
export function relatedOf(n: InboxNotification): RelatedItem[] {
  const out: RelatedItem[] = [];
  if (n.registration || n.truck) {
    out.push({
      key: 'truck',
      label: 'Truck',
      text: n.registration || String(n.truck).replace(/^(reg|asset):/, ''),
      ...(n.truck ? { filter: ['truck', n.truck] as [Dimension, string] } : {}),
    });
  }
  if (n.driver_name) {
    out.push({
      key: 'driver',
      label: 'Driver',
      text: n.driver_name,
      ...(n.driver_id ? { filter: ['driver', n.driver_id] as [Dimension, string] } : {}),
    });
  }
  if (n.trip_reference) out.push({ key: 'trip', label: 'Trip', text: n.trip_reference });
  if (n.route_name) {
    out.push({
      key: 'route',
      label: 'Route',
      text: n.route_name,
      ...(n.route_id ? { filter: ['route', n.route_id] as [Dimension, string] } : {}),
    });
  }
  if (n.site_name) {
    out.push({
      key: 'site',
      label: 'Site',
      text: n.site_name,
      ...(n.site_id != null ? { filter: ['site', String(n.site_id)] as [Dimension, string] } : {}),
    });
  }
  return out;
}

/** `incoming` (newest first) above `rows`, without repeats. */
export function mergeNewest(incoming: InboxNotification[], rows: InboxNotification[]): InboxNotification[] {
  const seen = new Set<string>();
  const out: InboxNotification[] = [];
  for (const n of [...incoming, ...rows]) {
    if (seen.has(n.id)) continue;
    seen.add(n.id);
    out.push(n);
  }
  return out;
}

/** Same-origin absolute links → app path; anything else → null (no in-app page to open). */
export function inAppPath(link: string | null | undefined): string | null {
  if (!link) return null;
  try {
    const u = new URL(link, window.location.origin);
    return u.origin === window.location.origin ? `${u.pathname}${u.search}${u.hash}` : null;
  } catch {
    return link.startsWith('/') ? link : null;
  }
}
